import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { formatCost, formatDate, formatInt } from '../format';

// LLM usage dashboard (ADR 0074): calls, tokens, and cost across every feature
// that uses the shared LLM client, aggregated by model and by purpose over a
// selectable window, plus the most recent calls. A neo_chat call links to its
// stored chat turn; the turn's question and answer are a user's private chat,
// so they load only after the operator gives a reason, which the server audits
// before returning anything (ADR 0228, ADR 0119).

interface UsageGroup {
  key: string;
  calls: number;
  errors: number;
  totalTokens: number;
  costUsd: number;
}

interface RecentCall {
  id: string;
  created_at: string;
  prompt: string | null;
  purpose: string;
  model: string;
  served_model: string | null;
  status: 'ok' | 'error';
  total_tokens: number | null;
  cost_usd: number | null;
  latency_ms: number | null;
  attempts: number;
  http_status: number | null;
  error: string | null;
  // 'length' on an ok row means the model hit its token budget before finishing
  // (a reasoning-budget truncation; ADR 0087) — flagged in the Status cell.
  finish_reason: string | null;
  // The stored Neo turn this neo_chat call belongs to; null for every other
  // purpose, for a turn that was never stored, and for calls before ADR 0228.
  neo_message_id: string | null;
}

// GET /v1/admin/neo/turns — one stored turn's question and answer.
interface NeoTurnExchange {
  message_id: string;
  conversation_id: string;
  user_id: string;
  question: string | null;
  answer: string;
  // 'ok', or why the model's reply was replaced by a fallback; null when the
  // turn has no enrichment row.
  output_gate: string | null;
  suppressed_content: string | null;
  created_at: string;
}

interface UsageSummary {
  windowDays: number;
  totals: {
    calls: number;
    errors: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    costUsd: number;
  };
  byModel: UsageGroup[];
  byPurpose: UsageGroup[];
  recent: RecentCall[];
}

const WINDOWS = [7, 30, 90];

function PromptSnapshot({ value }: { value: string | null }) {
  if (value === null || value.length === 0) return <>—</>;
  return (
    <details className="admin-usage-snapshot">
      <summary>Show prompt</summary>
      <pre>{value}</pre>
    </details>
  );
}

function NeoExchangeRow({ exchange, masked }: { exchange: NeoTurnExchange; masked: boolean }) {
  const textClass = masked
    ? 'admin-usage-exchange-text neo-admin-masked'
    : 'admin-usage-exchange-text';
  const gated = exchange.output_gate !== null && exchange.output_gate !== 'ok';
  return (
    <tr className="admin-usage-exchange">
      <td colSpan={8}>
        <div className="admin-usage-exchange-grid">
          <span className="admin-muted">Question</span>
          <div className={textClass}>{exchange.question ?? '—'}</div>
          <span className="admin-muted">Neo</span>
          <div>
            <div className={textClass}>{exchange.answer}</div>
            {gated ? (
              <details className="admin-usage-snapshot">
                <summary>Fallback shown ({exchange.output_gate}): model output</summary>
                <pre className={masked ? 'neo-admin-masked' : ''}>
                  {exchange.suppressed_content ?? '—'}
                </pre>
              </details>
            ) : null}
          </div>
          <span />
          <Link to={`/admin/neo/sessions/${exchange.conversation_id}`}>Open transcript</Link>
        </div>
      </td>
    </tr>
  );
}

export function AdminLlmUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-call log is operator-debugging detail — collapsed by default so the
  // page leads with the totals + breakdowns an executive scans for cost.
  const [showRecent, setShowRecent] = useState(false);
  // Neo questions & answers, by assistant message id; null for a turn the
  // server no longer has. Each load is audited, so loaded turns are kept across
  // window changes and only turns not yet requested are fetched.
  const [turns, setTurns] = useState<ReadonlyMap<string, NeoTurnExchange | null>>(new Map());
  const [reason, setReason] = useState('');
  const [turnsLoading, setTurnsLoading] = useState(false);
  const [turnsError, setTurnsError] = useState<string | null>(null);
  // Blurred until revealed, as in the Neo transcript (ADR 0119).
  const [revealPii, setRevealPii] = useState(false);

  useEffect(() => {
    // Guard against out-of-order resolution: clicking through windows quickly
    // fires overlapping requests, and a slower earlier one must not overwrite
    // the newer window's data.
    let ignore = false;
    setData(null);
    setError(null);
    adminApi<UsageSummary>(`/v1/admin/llm-usage?days=${days}`)
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load LLM usage.');
      });
    return () => {
      ignore = true;
    };
  }, [days]);

  // The stored turns behind this window's neo_chat calls, each shown once under
  // its newest call (a turn with tool rounds makes several calls).
  const firstCallOfTurn = new Map<string, string>();
  for (const c of data?.recent ?? []) {
    if (c.neo_message_id !== null && !firstCallOfTurn.has(c.neo_message_id)) {
      firstCallOfTurn.set(c.neo_message_id, c.id);
    }
  }
  const unloadedTurnIds = [...firstCallOfTurn.keys()].filter((id) => !turns.has(id));

  async function loadTurns(e: FormEvent) {
    e.preventDefault();
    if (unloadedTurnIds.length === 0 || reason.trim().length < 3) return;
    setTurnsLoading(true);
    setTurnsError(null);
    const requested = unloadedTurnIds;
    try {
      const res = await adminApi<{ turns: NeoTurnExchange[] }>(
        `/v1/admin/neo/turns?ids=${requested.join(',')}&reason=${encodeURIComponent(reason.trim())}`
      );
      setTurns((prev) => {
        const next = new Map(prev);
        for (const id of requested) next.set(id, null);
        for (const t of res.turns) next.set(t.message_id, t);
        return next;
      });
    } catch (err) {
      const e2 = err as { body?: { message?: string } };
      setTurnsError(e2.body?.message ?? 'Failed to load Neo questions and answers.');
    } finally {
      setTurnsLoading(false);
    }
  }

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>LLM usage</h1>
        <span className="admin-count admin-seg">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className="admin-link-button"
              disabled={w === days}
              onClick={() => setDays(w)}
            >
              {w}d
            </button>
          ))}
        </span>
      </header>
      <p className="admin-hint">
        Calls, tokens, and cost across every feature using the shared LLM client, over the last{' '}
        {days} days.
      </p>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        <>
          <dl className="admin-defs">
            <dt>Calls</dt>
            <dd>
              {formatInt(data.totals.calls)}
              {data.totals.errors > 0 ? ` (${formatInt(data.totals.errors)} errors)` : ''}
            </dd>
            <dt>Total tokens</dt>
            <dd>
              {formatInt(data.totals.totalTokens)} ({formatInt(data.totals.promptTokens)} in /{' '}
              {formatInt(data.totals.completionTokens)} out)
            </dd>
            {/* Lead figure, enlarged via .admin-defs-lead so spend dominates —
                shared with the Web search Usage page so the siblings match. This
                cost is OpenRouter-reported (not estimated), so unlike the search
                page it carries no "estimated" caveat. */}
            <dt>Cost</dt>
            <dd className="admin-defs-lead">{formatCost(data.totals.costUsd)}</dd>
          </dl>

          {/* By purpose leads (what the spend is FOR), then by model. */}
          <h2>By purpose</h2>
          {data.byPurpose.length === 0 ? (
            <p className="admin-empty">No calls in this window.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Calls</th>
                    <th>Errors</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byPurpose.map((g) => (
                    <tr key={g.key}>
                      <td>{g.key}</td>
                      <td>{formatInt(g.calls)}</td>
                      <td>{formatInt(g.errors)}</td>
                      <td>{formatInt(g.totalTokens)}</td>
                      <td>{formatCost(g.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>By model</h2>
          {data.byModel.length === 0 ? (
            <p className="admin-empty">No calls in this window.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Calls</th>
                    <th>Errors</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byModel.map((g) => (
                    <tr key={g.key}>
                      <td>{g.key}</td>
                      <td>{formatInt(g.calls)}</td>
                      <td>{formatInt(g.errors)}</td>
                      <td>{formatInt(g.totalTokens)}</td>
                      <td>{formatCost(g.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>
            Recent calls{' '}
            <button
              type="button"
              className="admin-link-button"
              onClick={() => setShowRecent((v) => !v)}
            >
              {showRecent ? 'Hide' : 'Show'}
            </button>
          </h2>
          {showRecent && firstCallOfTurn.size > 0 ? (
            unloadedTurnIds.length > 0 ? (
              <>
                <p className="admin-hint">
                  Neo chat questions and answers are a user&apos;s private conversation. Loading
                  them is logged with your account and your stated reason.
                </p>
                <form className="neo-admin-reason" onSubmit={(e) => void loadTurns(e)}>
                  <label htmlFor="llm-usage-neo-reason">Reason for viewing</label>
                  <input
                    id="llm-usage-neo-reason"
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. checking what drives Neo spend"
                    minLength={3}
                    maxLength={200}
                    required
                  />
                  <button
                    type="submit"
                    className="admin-button-secondary"
                    disabled={turnsLoading || reason.trim().length < 3}
                  >
                    {turnsLoading
                      ? 'Loading…'
                      : `Show questions & answers (${unloadedTurnIds.length} Neo ${
                          unloadedTurnIds.length === 1 ? 'turn' : 'turns'
                        })`}
                  </button>
                </form>
              </>
            ) : (
              <p className="admin-hint">
                Showing Neo questions and answers. Access logged.{' '}
                <button
                  type="button"
                  className="admin-link-button"
                  onClick={() => setRevealPii((v) => !v)}
                >
                  {revealPii ? 'Mask text' : 'Reveal text'}
                </button>
              </p>
            )
          ) : null}
          {showRecent && turnsError !== null ? (
            <div className="admin-auth-banner error">{turnsError}</div>
          ) : null}
          {showRecent ? (
            data.recent.length === 0 ? (
              <p className="admin-empty">No calls in this window.</p>
            ) : (
              <div className="scroll-x admin-usage-recent">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Purpose</th>
                      <th>Prompt</th>
                      <th>Model</th>
                      <th>Status</th>
                      <th>Tokens</th>
                      <th>Cost</th>
                      <th>Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((c) => {
                      const exchange =
                        c.neo_message_id !== null && firstCallOfTurn.get(c.neo_message_id) === c.id
                          ? (turns.get(c.neo_message_id) ?? undefined)
                          : undefined;
                      return (
                        <Fragment key={c.id}>
                          <tr
                            className={
                              exchange !== undefined ? 'admin-usage-call--with-exchange' : undefined
                            }
                          >
                            <td>{formatDate(c.created_at)}</td>
                            <td>{c.purpose}</td>
                            <td>
                              <PromptSnapshot value={c.prompt} />
                            </td>
                            <td>{c.served_model ?? c.model}</td>
                            <td>
                              {c.status}
                              {c.status === 'error' && c.http_status !== null
                                ? ` (${c.http_status})`
                                : ''}
                              {c.attempts > 1 ? ` ·${c.attempts}×` : ''}
                              {c.finish_reason === 'length' ? ' · truncated' : ''}
                            </td>
                            <td>{c.total_tokens !== null ? formatInt(c.total_tokens) : '—'}</td>
                            <td>{formatCost(c.cost_usd)}</td>
                            <td>{c.latency_ms !== null ? `${formatInt(c.latency_ms)} ms` : '—'}</td>
                          </tr>
                          {exchange !== undefined ? (
                            <NeoExchangeRow exchange={exchange} masked={!revealPii} />
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </>
      ) : null}
    </section>
  );
}
